import { useState, useEffect, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import { GameLayout, GameStatusAlert } from '@/components/games/common'
import { GameShortcuts } from '@/components/games/common/game-shortcuts'
import { Hash, X } from 'lucide-react'
import { saveGameResult, getUserStats, getGameResult, hasPlayedToday } from '@/lib/game-stats'
import { InContentAd } from '@/components/adsense'
import { getDateString } from '@/lib/dictionaries'

interface GameState {
  sequence: number[]
  userSequence: number[]
  gameStatus: 'ready' | 'showing' | 'playing' | 'correct' | 'won' | 'lost'
  currentLevel: number
  showingIndex: number
  maxLevel: number
  isFlashing: boolean
  flashingNumber: number | null
  failedAttemptsByLevel: { [level: number]: number }
  attemptsByLevel: { [level: number]: number }
  currentAttempt: number
  previousNumbers: number[] // Números usados en niveles anteriores
}

const numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]

// Función para generar una secuencia diaria completa
function generateDailySequence(language: string, maxLevel: number): number[] {
  const today = new Date()
  const dateString = getDateString(today)

  // Crear seed base
  let seed = 0
  const seedString = dateString + language
  for (let i = 0; i < seedString.length; i++) {
    seed = ((seed << 5) - seed + seedString.charCodeAt(i)) & 0xffffffff
  }

  // Función PRNG simple y efectiva
  const rng = (function (seed: number) {
    return function () {
      seed = (seed * 9301 + 49297) % 233280
      return seed / 233280
    }
  })(Math.abs(seed))

  const sequence: number[] = []

  for (let i = 0; i < maxLevel; i++) {
    const nextNumberIndex = Math.floor(rng() * numbers.length)
    sequence.push(numbers[nextNumberIndex])
  }

  return sequence
}


export function MemoriaNumerica() {
  const { t, language } = useTranslation()
  const [gameState, setGameState] = useState<GameState>({
    sequence: [],
    userSequence: [],
    gameStatus: 'ready',
    currentLevel: 3, // Empezar con nivel 3 (3 números)
    showingIndex: 0,
    maxLevel: 8, // Máximo nivel 8 (8 números)
    isFlashing: false,
    flashingNumber: null,
    failedAttemptsByLevel: {},
    attemptsByLevel: {},
    currentAttempt: 1,
    previousNumbers: [] // Números usados en niveles anteriores
  })

  const [, setIsNewGameMode] = useState(false)
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  const correctTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const today = getDateString()
  const [, setStats] = useState(getUserStats(`memoria-numerica-${language}`))

  const savePartialGameState = (state: GameState) => {
    if (state.gameStatus === 'playing' || state.gameStatus === 'ready' || state.gameStatus === 'showing' || state.gameStatus === 'correct') {
      const partialState = {
        sequence: state.sequence,
        userSequence: state.userSequence,
        gameStatus: state.gameStatus,
        currentLevel: state.currentLevel,
        showingIndex: state.showingIndex,
        failedAttemptsByLevel: state.failedAttemptsByLevel,
        attemptsByLevel: state.attemptsByLevel,
        currentAttempt: state.currentAttempt,
        previousNumbers: state.previousNumbers,
        date: today
      }
      localStorage.setItem(`game-memoria-numerica-${language}-partial`, JSON.stringify(partialState))
    }
  }

  const loadPartialGameState = (): Partial<GameState> | null => {
    const key = `game-memoria-numerica-${language}-partial`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (parsed.date === today) {
          return parsed
        } else {
          localStorage.removeItem(key)
          return null
        }
      } catch {
        return null
      }
    }
    return null
  }

  const clearPartialGameState = () => {
    localStorage.removeItem(`game-memoria-numerica-${language}-partial`)
  }

  useEffect(() => {
    setIsInitialLoadComplete(false)
    setStats(getUserStats(`memoria-numerica-${language}`))

    if (hasPlayedToday(`memoria-numerica-${language}`, today)) {
      const todayResult = getGameResult(`memoria-numerica-${language}`, today)
      if (todayResult) {
        if (todayResult.won) {
          const fullSequence = generateDailySequence(language, 8)
          setGameState(prev => ({
            ...prev,
            gameStatus: 'won',
            currentLevel: 8,
            sequence: fullSequence
          }))
        } else {
          const level = todayResult.attempts.length >= 3 ? todayResult.attempts.length : 3
          setGameState(prev => ({
            ...prev,
            gameStatus: 'lost',
            currentLevel: level,
            sequence: todayResult.attempts.map(a => typeof a === 'string' ? parseInt(a, 10) : a),
            previousNumbers: []
          }))
        }
        clearPartialGameState()
      }
    } else {
      const partialState = loadPartialGameState()
      if (partialState) {
        // Asegurar que el nivel mínimo sea 3
        const savedLevel = partialState.currentLevel || 3
        const correctedLevel = savedLevel < 3 ? 3 : savedLevel
        
        setGameState(prev => ({
          ...prev,
          sequence: partialState.sequence || [],
          userSequence: partialState.userSequence || [],
          gameStatus: partialState.gameStatus || 'ready',
          currentLevel: correctedLevel,
          showingIndex: partialState.showingIndex || 0,
          failedAttemptsByLevel: partialState.failedAttemptsByLevel || {},
          attemptsByLevel: partialState.attemptsByLevel || {},
          currentAttempt: partialState.currentAttempt || 1,
          previousNumbers: partialState.previousNumbers || []
        }))
      }
    }

    const hasSeenInstructions = localStorage.getItem(`game-memoria-numerica-${language}-instructions`)
    if (!hasSeenInstructions && window.innerWidth < 1024) {
      setShowFirstTimeModal(true)
    }

    setIsInitialLoadComplete(true)
  }, [language, today])

  useEffect(() => {
    if (!isInitialLoadComplete) return
    if (gameState.gameStatus === 'playing' || gameState.gameStatus === 'ready' || gameState.gameStatus === 'showing' || gameState.gameStatus === 'correct') {
      savePartialGameState(gameState)
    }
  }, [gameState, isInitialLoadComplete])

  const generateSequence = useCallback((level: number, previousNumbers: number[]) => {
    const sequence: number[] = []
    const today = new Date()
    const dateString = getDateString(today)
    
    // Crear seed base para este nivel
    let seed = 0
    const seedString = dateString + language + `level${level}`
    for (let i = 0; i < seedString.length; i++) {
      seed = ((seed << 5) - seed + seedString.charCodeAt(i)) & 0xffffffff
    }
    
    // Función PRNG
    let rngState = Math.abs(seed)
    const rng = () => {
      rngState = (rngState * 9301 + 49297) % 233280
      return rngState / 233280
    }
    
    // Generar números diferentes a los anteriores
    const usedNumbers = new Set(previousNumbers)
    const availableNumbers = numbers.filter(n => !usedNumbers.has(n))
    
    // Si no hay suficientes números disponibles (más de 10 niveles), permitir repetición pero evitar consecutivos
    for (let i = 0; i < level; i++) {
      let number: number
      if (availableNumbers.length > 0) {
        const randomIndex = Math.floor(rng() * availableNumbers.length)
        number = availableNumbers[randomIndex]
        // Remover el número usado de disponibles para este nivel
        availableNumbers.splice(randomIndex, 1)
      } else {
        // Si no hay disponibles, usar cualquier número (solo si hay más de 10 niveles)
        number = numbers[Math.floor(rng() * numbers.length)]
      }
      sequence.push(number)
    }
    
    return sequence
  }, [language])

  const startGame = () => {
    setGameState(prev => {
      // Asegurar que el nivel mínimo sea 3
      const level = prev.currentLevel < 3 ? 3 : prev.currentLevel
      const sequence = generateSequence(level, prev.previousNumbers)
      const newState: GameState = {
        ...prev,
        currentLevel: level,
        sequence,
        userSequence: [],
        gameStatus: 'showing' as const,
        showingIndex: 0,
        isFlashing: false,
        flashingNumber: null,
        currentAttempt: 1
      }
      savePartialGameState(newState)
      return newState
    })
  }

  useEffect(() => {
    if (gameState.gameStatus !== 'showing') return
    if (gameState.sequence.length === 0) return

    let timeoutId: NodeJS.Timeout
    const sequenceLength = gameState.sequence.length

    // Mostrar todos los números a la vez
    setGameState(prev => ({
      ...prev,
      isFlashing: true,
      flashingNumber: null // null significa mostrar todos
    }))

    // Después de mostrar todos los números, pasar a playing
    timeoutId = setTimeout(() => {
      setGameState(prev => ({
        ...prev,
        gameStatus: 'playing',
        isFlashing: false,
        flashingNumber: null
      }))
    }, sequenceLength * 600 + 500) // 600ms por número + 500ms extra

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [gameState.gameStatus === 'showing', gameState.sequence.length])

  const handleNumberClick = (number: number) => {
    if (gameState.gameStatus !== 'playing') return

    setGameState(prev => {
      const newUserSequence = [...prev.userSequence, number]
      
      if (newUserSequence.length === prev.sequence.length) {
        const isCorrect = newUserSequence.every((num, index) => num === prev.sequence[index])

        if (isCorrect) {
          const newAttemptsByLevel = {
            ...prev.attemptsByLevel,
            [prev.currentLevel]: (prev.attemptsByLevel[prev.currentLevel] || 0) + 1
          }

          // Agregar los números actuales a los números previos
          const newPreviousNumbers = [...prev.previousNumbers, ...prev.sequence]

          if (prev.currentLevel >= prev.maxLevel) {
            // Ganó - usar la secuencia actual (nivel 8) para mostrar
            const totalAttempts = Object.values(newAttemptsByLevel).reduce((sum, attempts) => sum + (attempts || 0), 0)
            saveGameResult(`memoria-numerica-${language}`, prev.sequence.map(n => n.toString()), true, today, {
              level: prev.maxLevel,
              attemptsByLevel: newAttemptsByLevel,
              totalAttempts
            })
            setStats(getUserStats(`memoria-numerica-${language}`))
            setIsNewGameMode(false)
            clearPartialGameState()

            toast.success(t("games.memoriaNumerica.congratulations"), {
              duration: 4000
            })

            return {
              ...prev,
              userSequence: newUserSequence,
              gameStatus: 'won',
              attemptsByLevel: newAttemptsByLevel,
              previousNumbers: newPreviousNumbers
            }
          } else {
            // Mostrar mensaje "correcto" antes de pasar al siguiente nivel
            // Limpiar timeout anterior si existe
            if (correctTimeoutRef.current) {
              clearTimeout(correctTimeoutRef.current)
            }
            
            const currentLevel = prev.currentLevel
            correctTimeoutRef.current = setTimeout(() => {
              setGameState(current => {
                // Usar el nivel guardado en el closure para evitar problemas de estado
                const nextLevel = currentLevel + 1
                const newState: GameState = {
                  ...current,
                  currentLevel: nextLevel,
                  userSequence: [],
                  gameStatus: 'ready' as const,
                  attemptsByLevel: newAttemptsByLevel,
                  currentAttempt: 1,
                  previousNumbers: newPreviousNumbers
                }
                savePartialGameState(newState)
                correctTimeoutRef.current = null
                return newState
              })
            }, 1000) // Esperar 1 segundo para mostrar "correcto"

            return {
              ...prev,
              userSequence: newUserSequence,
              gameStatus: 'correct' as const,
              attemptsByLevel: newAttemptsByLevel,
              previousNumbers: newPreviousNumbers
            }
          }
      } else {
          // Secuencia incorrecta
          const newFailedAttempts = {
            ...prev.failedAttemptsByLevel,
            [prev.currentLevel]: (prev.failedAttemptsByLevel[prev.currentLevel] || 0) + 1
          }

          if (prev.currentAttempt >= 3) {
            const sequence = generateSequence(prev.currentLevel, prev.previousNumbers)
            saveGameResult(`memoria-numerica-${language}`, sequence.map(n => n.toString()), false, today, {
              level: prev.currentLevel
            })
            setStats(getUserStats(`memoria-numerica-${language}`))
            clearPartialGameState()

            toast.error(t("games.memoriaNumerica.youLost", { level: prev.currentLevel }))

            return {
              ...prev,
              userSequence: newUserSequence,
              gameStatus: 'lost',
              failedAttemptsByLevel: newFailedAttempts,
              sequence
            }
          } else {
            setTimeout(() => {
              setGameState(current => {
                const newState: GameState = {
                  ...current,
                  userSequence: [],
                  gameStatus: 'ready' as const,
                  failedAttemptsByLevel: newFailedAttempts,
                  currentAttempt: current.currentAttempt + 1
                }
                savePartialGameState(newState)
                return newState
              })
            }, 1000)

            return {
              ...prev,
              userSequence: newUserSequence,
              failedAttemptsByLevel: newFailedAttempts
            }
          }
        }
      }
      
      // Si aún no ha completado la secuencia, solo actualizar userSequence
      return { ...prev, userSequence: newUserSequence }
    })
  }

  const showSequenceAgain = () => {
    if (gameState.sequence.length > 0) {
      setGameState(prev => ({
        ...prev,
        gameStatus: 'showing',
        userSequence: [],
        isFlashing: false,
        flashingNumber: null
      }))
    }
  }

  const handleDeleteLast = () => {
    if (gameState.gameStatus !== 'playing') return
    if (gameState.userSequence.length === 0) return

    setGameState(prev => ({
      ...prev,
      userSequence: prev.userSequence.slice(0, -1)
    }))
  }

  const handleCloseFirstTimeModal = () => {
    setShowFirstTimeModal(false)
    localStorage.setItem(`game-memoria-numerica-${language}-instructions`, 'true')
  }

  const renderGameContent = () => (
    <>
      {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') && (
        <GameStatusAlert status={gameState.gameStatus}>
          <div className="space-y-1">
            <div className="font-bold">
              {gameState.gameStatus === 'won'
                ? t("games.memoriaNumerica.youWon", { level: gameState.maxLevel })
                : t("games.memoriaNumerica.youLost", { level: gameState.currentLevel })
              }
            </div>
            <div className="text-xs font-medium">
              {t("games.memoriaNumerica.comeBackTomorrow")}
            </div>
          </div>
        </GameStatusAlert>
      )}

      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-lg lg:text-xl font-bold">{t("games.memoriaNumerica.title")}</h2>
          <p className="text-xs lg:text-sm text-muted-foreground">{t("games.memoriaNumerica.subtitle")}</p>
          <div className="flex items-center justify-center gap-2 lg:gap-4 text-xs lg:text-sm">
            <Badge variant="outline">
              {t("games.memoriaNumerica.level")} {gameState.currentLevel}
            </Badge>
            <Badge variant="outline">
              {t("statistics.maxStreak")}: {gameState.maxLevel}
            </Badge>
            {gameState.gameStatus === 'playing' && (
              <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {t("games.memoriaNumerica.attempt")} {gameState.currentAttempt}/3
              </Badge>
            )}
          </div>
        </div>

        {gameState.gameStatus === 'ready' && (
          <div className="text-center space-y-3 lg:space-y-4">
            <p className="text-base lg:text-lg font-medium">{t("games.memoriaNumerica.clickStart")}</p>
            <Button onClick={startGame} size="lg" className="px-6 lg:px-8">
              {gameState.currentLevel === 3 ? t("games.memoriaNumerica.startGame") : t("games.memoriaNumerica.nextLevel")}
            </Button>
          </div>
        )}

        {gameState.gameStatus === 'correct' && (
          <div className="text-center space-y-2">
            <div className="text-sm lg:text-base font-medium text-green-600 dark:text-green-400 opacity-70">
              ✓ {t("games.memoriaNumerica.correct")}
            </div>
          </div>
        )}

        {gameState.gameStatus === 'showing' && (
          <div className="text-center space-y-3 lg:space-y-4">
            <p className="text-base lg:text-lg font-medium">{t("games.memoriaNumerica.watchSequence")}</p>
            <Button onClick={showSequenceAgain} variant="outline" size="sm">
              {t("games.memoriaNumerica.showSequence")}
            </Button>
          </div>
        )}

        {gameState.gameStatus === 'playing' && (
          <div className="text-center space-y-3 lg:space-y-4">
            <p className="text-base lg:text-lg font-medium">{t("games.memoriaNumerica.repeatSequence")}</p>
            <p className="text-xs lg:text-sm text-muted-foreground">{t("games.memoriaNumerica.yourSequence")}</p>
          </div>
        )}

        {/* Number Display */}
        {(gameState.gameStatus === 'showing' || gameState.gameStatus === 'playing' || gameState.gameStatus === 'ready') && (
          <div className="text-center w-full overflow-hidden">
            {gameState.gameStatus === 'showing' && gameState.isFlashing ? (
              // Mostrar todos los números de la secuencia a la vez
              <div className="flex justify-center gap-2 lg:gap-3 flex-wrap px-4">
                {gameState.sequence.map((num, index) => (
                  <div
                    key={index}
                    className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-primary animate-pulse flex-shrink-0"
                  >
                    {num}
                  </div>
                ))}
              </div>
            ) : gameState.gameStatus === 'playing' && gameState.userSequence.length > 0 ? (
              // Mostrar todos los números de la secuencia del usuario arriba
              <div className="flex justify-center gap-2 lg:gap-3 flex-wrap px-4">
                {gameState.userSequence.map((num, index) => (
                  <div
                    key={index}
                    className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-primary flex-shrink-0"
                  >
                    {num}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-6xl lg:text-7xl font-bold text-muted-foreground">?</div>
            )}
          </div>
        )}

        {/* Number Grid */}
        {gameState.gameStatus === 'playing' && (
          <div className="space-y-3">
            <div className="grid grid-cols-5 gap-2 lg:gap-3 max-w-md mx-auto">
              {numbers.map((number) => (
                <button
                  key={number}
                  onClick={() => handleNumberClick(number)}
                  className="aspect-square rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 text-2xl lg:text-3xl font-bold"
                >
                  {number}
                </button>
              ))}
            </div>
            {gameState.userSequence.length > 0 && (
              <div className="flex justify-center">
                <Button
                  onClick={handleDeleteLast}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <X className="h-4 w-4" />
                  {t("games.memoriaNumerica.deleteLast") || "Borrar último"}
                </Button>
              </div>
            )}
          </div>
        )}


        {/* Show sequence when already played today */}
        {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') && gameState.sequence.length > 0 && (
          <div className="text-center space-y-2 lg:space-y-3">
            <div className="text-xs lg:text-sm text-muted-foreground">
              {t("games.memoriaNumerica.todaysSequence")}:
            </div>
            <div className="flex justify-center gap-2">
              {gameState.sequence.map((num, index) => (
                <div
                  key={index}
                  className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-xl lg:text-2xl font-bold border-2 border-gray-300 shadow-md"
                >
                  {num}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )

  return (
    <GameLayout>
      <div className="hidden lg:flex lg:flex-row lg:gap-8 lg:px-8 lg:py-8">
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className="w-full max-w-xl xl:max-w-2xl">
            {renderGameContent()}
          </div>
        </div>

        <div className="w-80 min-w-80 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Hash className="h-4 w-4" />
                {t("games.memoriaNumerica.howToPlay")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs">{t("games.memoriaNumerica.rules.main")}</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-xs">{t("games.memoriaNumerica.rules.watch")}</Badge>
                  <span className="text-xs">{t("games.memoriaNumerica.rules.watchDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-xs">{t("games.memoriaNumerica.rules.repeat")}</Badge>
                  <span className="text-xs">{t("games.memoriaNumerica.rules.repeatDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground text-xs">{t("games.memoriaNumerica.rules.advance")}</Badge>
                  <span className="text-xs">{t("games.memoriaNumerica.rules.advanceDesc")}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <InContentAd className="w-full" compact />

          <GameShortcuts currentGame="memoria-numerica" />
        </div>
      </div>

      <div className="lg:hidden min-h-[calc(100dvh-64px)] flex flex-col">
        <InContentAd className="w-full" compact />

        <div className="flex-1 flex flex-col py-2 overflow-y-auto">
          <div className="w-full py-4">
            {renderGameContent()}

            {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost' || hasPlayedToday(`memoria-numerica-${language}`, today)) && (
              <GameShortcuts currentGame="memoria-numerica" />
            )}
          </div>
        </div>
      </div>

      <Dialog open={showFirstTimeModal} onOpenChange={setShowFirstTimeModal}>
        <DialogContent className="max-w-sm mx-auto mt-20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              {t("games.memoriaNumerica.howToPlay")}
            </DialogTitle>
            <DialogDescription className="text-left space-y-3">
              <p className="text-sm">{t("games.memoriaNumerica.rules.main")}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-500 text-xs">{t("games.memoriaNumerica.rules.watch")}</Badge>
                  <span className="text-xs">{t("games.memoriaNumerica.rules.watchDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500 text-xs">{t("games.memoriaNumerica.rules.repeat")}</Badge>
                  <span className="text-xs">{t("games.memoriaNumerica.rules.repeatDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-500 text-xs">{t("games.memoriaNumerica.rules.advance")}</Badge>
                  <span className="text-xs">{t("games.memoriaNumerica.rules.advanceDesc")}</span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={handleCloseFirstTimeModal}>
              {t("navigation.gotIt")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </GameLayout>
  )
}
